import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import DashboardNavbar from "../DashboardNavbar";
import { useLoading } from "../LoadingContext";

const CandidateVerificationForm = () => {
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const navigate = useNavigate();
  const { id } = useParams(); //  ID passed in route
  const { loading, setLoading } = useLoading();
  const [personalInfo, setPersonalInfo] = useState({});
  const [emergencyContact1, setEmergencyContact1] = useState({});
  const [emergencyContact2, setEmergencyContact2] = useState({});
  const [employmentHistory, setEmploymentHistory] = useState([]);
  const [educationHistory, setEducationHistory] = useState([]);
  const [uploadedDocuments, setUploadedDocuments] = useState({
    experience_letters: [],
    relieving_letters: [],
  });

  const [primarySkills, setPrimarySkills] = useState([]);
  const [secondarySkills, setSecondarySkills] = useState([]);
  const [criminalRecord, setCriminalRecord] = useState({});
  const [healthRecord, setHealthRecord] = useState({});

  // Verification states for HR
  const [verifications, setVerifications] = useState({
    personalInfo: "Not Verified",
    emergencyContacts: "Not Verified",
    employmentHistory: "Not Verified",
    educationHistory: "Not Verified",
    skills: "Not Verified",
    documents: "Not Verified",
    criminalRecord: "Not Verified",
    healthRecord: "Not Verified",
  });

  // Fetch candidate data from backend
  // useEffect(() => {
  //   const fetchCandidateData = async () => {
  //     setLoading(true);
  //     try {
  //       const response = await fetch(
  //         `${process.env.REACT_APP_API_BASE_URL}/bgv_form/personal-info-det/${id}/`
  //       );
  //       if (!response.ok) {
  //         throw new Error("Failed to fetch candidate data.");
  //       }
  //       const data = await response.json();
  //       console.log("Fetched Data:", data);

  //       setPersonalInfo(data.personal_info || {});
  //       setEmergencyContact1(data.emergency_contact?.[0] || {});
  //       setEmergencyContact2(data.emergency_contact?.[1] || {});
  //       setEmploymentHistory(data.employment_history || []);
  //       setEducationHistory(data.education_history || []);

  //       // Extract uploaded documents
  //       setUploadedDocuments({
  //         ...data.uploaded_documents?.[0], // Assuming uploaded_documents contains one object
  //         experience_letters:
  //           data.experience_relieving_documents
  //             ?.filter((doc) => doc.document_type === "experience")
  //             ?.map((doc) => doc.file) || [],
  //         relieving_letters:
  //           data.experience_relieving_documents
  //             ?.filter((doc) => doc.document_type === "relieving")
  //             ?.map((doc) => doc.file) || [],
  //       });

  //       setPrimarySkills(
  //         data.skills?.filter((s) => s.skill_type === "primary") || []
  //       );
  //       setSecondarySkills(
  //         data.skills?.filter((s) => s.skill_type === "secondary") || []
  //       );
  //       setCriminalRecord(data.criminal_record?.[0] || {});
  //       setHealthRecord(data.health_record?.[0] || {});
  //     } catch (error) {
  //       console.error("Error fetching candidate data:", error);
  //     } finally {
  //       setLoading(false);
  //     }
  //   };

  //   if (id) {
  //     fetchCandidateData();
  //   }
  // }, [id]);

  useEffect(() => {
    setLoading(true);
    const fetchCandidateData = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/bgv_form/personal-info-det/${id}/`
        );
        if (!response.ok) {
          throw new Error("Failed to fetch candidate data.");
        }
        const data = await response.json();
        console.log("Fetched Data:", data);

        setPersonalInfo(data.personal_info || {});
        setEmergencyContact1(data.emergency_contact?.[0] || {});
        setEmergencyContact2(data.emergency_contact?.[1] || {});
        setEmploymentHistory(data.employment_history || []);
        setEducationHistory(data.education_history || []);

        setUploadedDocuments({
          ...data.uploaded_documents?.[0],
          experience_letters:
            data.experience_relieving_documents
              ?.filter((doc) => doc.document_type === "experience")
              ?.map((doc) => doc.file) || [],
          relieving_letters:
            data.experience_relieving_documents
              ?.filter((doc) => doc.document_type === "relieving")
              ?.map((doc) => doc.file) || [],
        });

        setPrimarySkills(
          data.skills?.filter((s) => s.skill_type === "primary") || []
        );
        setSecondarySkills(
          data.skills?.filter((s) => s.skill_type === "secondary") || []
        );
        setCriminalRecord(data.criminal_record?.[0] || {});
        setHealthRecord(data.health_record?.[0] || {});

        // Update the verification statuses based on the fetched data
        setVerifications({
          personalInfo: data.personal_info?.hr_verified
            ? "Verified"
            : "Not Verified",
          emergencyContacts: data.emergency_contact?.every(
            (contact) => contact.hr_verified
          )
            ? "Verified"
            : "Not Verified",
          employmentHistory: data.employment_history?.every(
            (employment) => employment.hr_verified
          )
            ? "Verified"
            : "Not Verified",
          educationHistory: data.education_history?.every(
            (education) => education.hr_verified
          )
            ? "Verified"
            : "Not Verified",
          skills: data.skills?.every((skill) => skill.hr_verified)
            ? "Verified"
            : "Not Verified",
          documents: data.uploaded_documents?.[0]?.hr_verified
            ? "Verified"
            : "Not Verified",
          criminalRecord: data.criminal_record?.[0]?.hr_verified
            ? "Verified"
            : "Not Verified",
          healthRecord: data.health_record?.[0]?.hr_verified
            ? "Verified"
            : "Not Verified",
        });
      } catch (error) {
        console.error("Error fetching candidate data:", error);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchCandidateData();
    }
  }, [id]);

  const handleVerificationChange = (section, status) => {
    setVerifications((prev) => ({
      ...prev,
      [section]: status,
    }));
  };

  const handleSubmitVerification = async () => {
    try {
      const sectionApis = {
        personalInfo: `${process.env.REACT_APP_API_BASE_URL}/bgv_form/personal-info/`,
        emergencyContacts: `${process.env.REACT_APP_API_BASE_URL}/bgv_form/emergency-contacts/`,
        employmentHistory: `${process.env.REACT_APP_API_BASE_URL}/bgv_form/employment-history/`,
        educationHistory: `${process.env.REACT_APP_API_BASE_URL}/bgv_form/education-history/`,
        documents: `${process.env.REACT_APP_API_BASE_URL}/bgv_form/uploaded-documents/`,
        criminalRecord: `${process.env.REACT_APP_API_BASE_URL}/bgv_form/criminal-record/`,
        healthRecord: `${process.env.REACT_APP_API_BASE_URL}/bgv_form/health-record/`,
        skills: `${process.env.REACT_APP_API_BASE_URL}/bgv_form/skillview/`,
      };

      // Update HR verification for each section
      for (const section in sectionApis) {
        // Fetch the HR verification status dynamically
        const isSectionVerified = verifications[section] === "Verified";

        // Create the payload dynamically
        const payload = { id: Number(id), hr_verified: isSectionVerified };

        console.log(`Payload for ${section}:`, payload);
        console.log(
          `Request to ${sectionApis[section]} with payload:`,
          payload
        );
        // Send the PATCH request for the section
        const sectionResponse = await fetch(`${sectionApis[section]}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        const sectionData = await sectionResponse.json();
        console.log(`Response from ${sectionApis[section]}:`, sectionData);

        if (!sectionResponse.ok) {
          const error = await sectionResponse.json();
          throw new Error(
            `Error updating HR verification for ${section}: ${JSON.stringify(
              error
            )}`
          );
        }
      }
      alert("Verification successfully submitted!");
      navigate("/employee_dashboard");
    } catch (error) {
      console.error("Error during verification submission:", error);
      alert(`Verification failed: ${error.message}`);
    }
  };

  if (loading) {
    return (
      <div
        className="container d-flex justify-content-center align-items-center"
        style={{ height: "100vh" }}
      >
        <div>
          <div
            className="spinner-border text-dark"
            role="status"
            style={{ width: "3rem", height: "3rem" }}
          >
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
        <h3 className="text-center text-dark">&nbsp;Data is Fetching.... </h3>
      </div>
    );
  }

  return (
    <>
      <DashboardNavbar />
      <div className="container my-3">
        <h2 className="text-center">Employee Onboarding Form</h2>
        <form>
          {/* Personal Information */}
          <section className="form-section">
            <h3 className="section-title">Personal Information</h3>
            <div className="row mb-2">
              {/* Your existing fields for personal information */}
              {/* Candidate Information */}
              <div className="row mb-2">
                <div className="col-md-2 d-flex align-items-start text-center">
                  <h5 className="mt-5">Candidate Details</h5>
                </div>
                <div className="col-md-10">
                  <div className="row">
                    <div className="col-md-3 mb-2">
                      <input
                        type="text"
                        name="first_name"
                        className="form-control"
                        placeholder="First Name"
                        value={personalInfo.first_name || ""}
                        readOnly
                      />
                    </div>
                    <div className="col-md-3 mb-2">
                      <input
                        type="text"
                        name="middle_name"
                        className="form-control"
                        placeholder="Middle Name"
                        value={personalInfo.middle_name || ""}
                        readOnly
                      />
                    </div>
                    <div className="col-md-3 mb-2">
                      <input
                        type="text"
                        name="last_name"
                        className="form-control"
                        placeholder="Last Name"
                        value={personalInfo.last_name || ""}
                        readOnly
                      />
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-3 mb-2">
                      <input
                        type="email"
                        name="email_id"
                        className="form-control"
                        placeholder="Email ID"
                        value={personalInfo.email_id || ""}
                        readOnly
                      />
                    </div>
                    <div className="col-md-3 mb-2">
                      <input
                        type="tel"
                        name="phone_number"
                        className="form-control"
                        placeholder="Phone Number"
                        value={personalInfo.phone_number || ""}
                        readOnly
                      />
                    </div>
                    <div className="col-md-3 mb-2">
                      <input
                        type="text"
                        name="marital_status"
                        className="form-control"
                        placeholder="Marital Status"
                        value={personalInfo.marital_status || ""}
                        readOnly
                      />
                    </div>

                    <div className="col-md-3 mb-2">
                      <input
                        type="text"
                        name="gender"
                        className="form-control"
                        placeholder="Gender"
                        value={personalInfo.gender || ""}
                        readOnly
                      />
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-3 mb-2">
                      <input
                        type="text"
                        name="blood_group"
                        className="form-control"
                        placeholder="Blood Group"
                        value={personalInfo.blood_group || ""}
                        readOnly
                      />
                    </div>
                    <div className="col-md-3 mb-2">
                      <input
                        type="text"
                        name="nationality"
                        className="form-control"
                        placeholder="Nationality"
                        value={personalInfo.nationality || ""}
                        readOnly
                      />
                    </div>
                    <div className="col-md-3 mb-2">
                      <textarea
                        name="current_address"
                        className="form-control"
                        placeholder="Current Address"
                        rows={1}
                        value={personalInfo.current_address || ""}
                        readOnly
                      ></textarea>
                    </div>
                    <div className="col-md-3 mb-2">
                      <textarea
                        name="permanent_address"
                        className="form-control"
                        placeholder="Permanent Address"
                        rows={1}
                        value={personalInfo.permanent_address || ""}
                        readOnly
                      ></textarea>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-3 mb-2">
                      <input
                        type="text"
                        name="aadhar_number"
                        className="form-control"
                        placeholder="Aadhar Number"
                        value={personalInfo.aadhar_number || ""}
                        readOnly
                      />
                    </div>
                    <div className="col-md-3 mb-2">
                      <input
                        type="text"
                        name="pan_number"
                        className="form-control"
                        placeholder="Pan Number"
                        value={personalInfo.pan_number || ""}
                        readOnly
                      />
                    </div>
                  </div>
                  <div className="row text-center">
                    <div className="col-md-3">
                      <label htmlFor="date_of_birth" className="form-label">
                        Date of Birth
                      </label>
                      <input
                        type="date"
                        name="date_of_birth"
                        className="form-control"
                        placeholder="Date of Birth"
                        value={personalInfo.date_of_birth || ""}
                        readOnly
                      />
                    </div>
                    <div className="col-md-3">
                      <label htmlFor="date_of_joining" className="form-label">
                        Date of Joining
                      </label>
                      <input
                        type="date"
                        name="date_of_joining"
                        className="form-control"
                        placeholder="Date of Joining"
                        value={personalInfo.date_of_joining || ""}
                        readOnly
                      />
                    </div>
                    <div className="col-md-3">
                      <label htmlFor="anniversary_day" className="form-label">
                        Anniversary Day
                      </label>
                      <input
                        type="date"
                        name="anniversary_day"
                        className="form-control"
                        placeholder="Anniversary Day"
                        value={personalInfo.anniversary_day || ""}
                        readOnly
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Father's Information */}
              <div className="row">
                <div className="col-md-2 d-flex align-items-start">
                  <h5 className="mt-1">Father's Details</h5>
                </div>
                <div className="col-md-10">
                  <div className="row">
                    <div className="col-md-3 mb-2">
                      <input
                        type="text"
                        name="father_name"
                        className="form-control"
                        placeholder="Father's Name"
                        value={personalInfo.father_name || ""}
                        readOnly
                      />
                    </div>
                    <div className="col-md-3 mb-2">
                      <input
                        type="text"
                        name="father_phone_number"
                        className="form-control"
                        placeholder="Father's Phone Number"
                        value={personalInfo.father_phone_number || ""}
                        readOnly
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Mother's Information */}
              <div className="row">
                <div className="col-md-2 d-flex align-items-start">
                  <h5 className="mt-1">Mother's Details</h5>
                </div>
                <div className="col-md-10">
                  <div className="row">
                    <div className="col-md-3 mb-2">
                      <input
                        type="text"
                        name="mother_name"
                        className="form-control"
                        placeholder="Mother's Name"
                        value={personalInfo.mother_name || ""}
                        readOnly
                      />
                    </div>
                    <div className="col-md-3 mb-2">
                      <input
                        type="text"
                        name="mother_phone_number"
                        className="form-control"
                        placeholder="Mother's Phone Number"
                        value={personalInfo.mother_phone_number || ""}
                        readOnly
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Spouse's Information */}
              <div className="row">
                <div className="col-md-2 d-flex align-items-start">
                  <h5 className="mt-1">Spouse's Details</h5>
                </div>
                <div className="col-md-10">
                  <div className="row">
                    <div className="col-md-3 mb-2">
                      <input
                        type="text"
                        name="spouse_name"
                        className="form-control"
                        placeholder="Spouse's Name"
                        value={personalInfo.spouse_name || ""}
                        readOnly
                      />
                    </div>
                    <div className="col-md-3 mb-2">
                      <input
                        type="text"
                        name="spouse_phone_number"
                        className="form-control"
                        placeholder="Phone Number"
                        value={personalInfo.spouse_phone_number || ""}
                        readOnly
                      />
                    </div>

                    <div className="col-md-3 mb-2">
                      <input
                        type="text"
                        name="spouse_blood_group"
                        className="form-control"
                        placeholder="Blood Group"
                        value={personalInfo.spouse_blood_group || ""}
                        readOnly
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Child Information */}
              <div className="row">
                <div className="col-md-2 d-flex align-items-start">
                  <h5 className="mt-1">Child Details</h5>
                </div>
                <div className="col-md-10">
                  <div className="row">
                    <div className="col-md-3 mb-2">
                      <input
                        type="text"
                        name="child1_name"
                        className="form-control"
                        placeholder="Child 1 Name"
                        value={personalInfo.child1_name || ""}
                        readOnly
                      />
                    </div>

                    <div className="col-md-3">
                      <input
                        type="text"
                        name="child2_name"
                        className="form-control"
                        placeholder="Child 2 Name"
                        value={personalInfo.child2_name || ""}
                        readOnly
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Verification */}
            <div className="mt-3 text-end">
              <h6 className="text-end">HR Verification</h6>
              <div className="form-check form-check-inline">
                <input
                  className="form-check-input border-dark"
                  type="radio"
                  name="personalInfoVerification"
                  id="personalInfoVerified"
                  checked={verifications.personalInfo === "Verified"}
                  onChange={() =>
                    handleVerificationChange("personalInfo", "Verified")
                  }
                />
                <label
                  className="form-check-label"
                  htmlFor="personalInfoVerified"
                >
                  Verified
                </label>
              </div>
              <div className="form-check form-check-inline">
                <input
                  className="form-check-input border-dark"
                  type="radio"
                  name="personalInfoVerification"
                  id="personalInfoNotVerified"
                  checked={verifications.personalInfo === "Not Verified"}
                  onChange={() =>
                    handleVerificationChange("personalInfo", "Not Verified")
                  }
                />
                <label
                  className="form-check-label"
                  htmlFor="personalInfoNotVerified"
                >
                  Not Verified
                </label>
              </div>
            </div>
          </section>

          <hr />

          {/* Emergency Contact section */}
          <section className="form-section">
            <h3>Emergency Contacts</h3>
            {/* Emergency contacts fields */}
            <div className="row">
              <div className="col-md-2 d-flex align-items-start">
                <h5 className="">Emergency Contact 1</h5>
              </div>
              <div className="col-md-10">
                <div className="row">
                  <div className="col-md-3 mb-2">
                    <div className="form-group">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Name"
                        name="name"
                        value={emergencyContact1.name || ""}
                        readOnly
                      />
                    </div>
                  </div>
                  <div className="col-md-3 mb-2">
                    <div className="form-group">
                      <input
                        type="tel"
                        className="form-control"
                        placeholder="Phone Number"
                        name="phone_number"
                        value={emergencyContact1.phone_number || ""}
                        readOnly
                      />
                    </div>
                  </div>
                  <div className="col-md-3 mb-2">
                    <div className="form-group">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Relation"
                        name="relation"
                        value={emergencyContact1.relation || ""}
                        readOnly
                      />
                    </div>
                  </div>
                  <div className="col-md-3">
                    <input
                      type="text"
                      name="language"
                      className="form-control"
                      placeholder="Preferred Language"
                      value={emergencyContact1.language || ""}
                      readOnly
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Second Emergency Contact */}
            <div className="row mt-1">
              <div className="col-md-2 d-flex align-items-start">
                <h5 className="">Emergency Contact 2</h5>
              </div>
              <div className="col-md-10">
                <div className="row">
                  <div className="col-md-3 mb-2">
                    <div className="form-group">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Name"
                        name="name"
                        value={emergencyContact2.name || ""}
                        readOnly
                      />
                    </div>
                  </div>
                  <div className="col-md-3 mb-2">
                    <div className="form-group">
                      <input
                        type="tel"
                        className="form-control"
                        placeholder="Phone Number"
                        name="phone_number"
                        value={emergencyContact2.phone_number || ""}
                        readOnly
                      />
                    </div>
                  </div>
                  <div className="col-md-3 mb-2">
                    <div className="form-group">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Relation"
                        name="relation"
                        value={emergencyContact2.relation || ""}
                        readOnly
                      />
                    </div>
                  </div>
                  <div className="col-md-3">
                    <input
                      type="text"
                      name="language"
                      className="form-control"
                      placeholder="Preferred Language"
                      value={emergencyContact2.language || ""}
                      readOnly
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-3 text-end">
              <h6>HR Verification</h6>
              <div className="form-check form-check-inline">
                <input
                  className="form-check-input border-dark"
                  type="radio"
                  name="emergencyContactsVerification"
                  id="emergencyContactsVerified"
                  checked={verifications.emergencyContacts === "Verified"}
                  onChange={() =>
                    handleVerificationChange("emergencyContacts", "Verified")
                  }
                />
                <label
                  className="form-check-label"
                  htmlFor="emergencyContactsVerified"
                >
                  Verified
                </label>
              </div>
              <div className="form-check form-check-inline">
                <input
                  className="form-check-input border-dark"
                  type="radio"
                  name="emergencyContactsVerification"
                  id="emergencyContactsNotVerified"
                  checked={verifications.emergencyContacts === "Not Verified"}
                  onChange={() =>
                    handleVerificationChange(
                      "emergencyContacts",
                      "Not Verified"
                    )
                  }
                />
                <label
                  className="form-check-label"
                  htmlFor="emergencyContactsNotVerified"
                >
                  Not Verified
                </label>
              </div>
            </div>
          </section>

          <hr />

          {/* Add similar sections for Employment, Education, Documents, etc. */}

          <section className="form-section">
            <h4 className="section-title">Employment History </h4>
            {employmentHistory.map((employment, index) => (
              <div key={index} className="border-none p-3 mb-3 row">
                <div className="col-md-2 d-flex align-items-start">
                  <h5 className="section-subtitle">
                    {index === 0
                      ? "Current Employment"
                      : `Previous Employment ${index}`}{" "}
                  </h5>
                </div>
                <div className="col-md-10">
                  <div className="row">
                    <div className="col-md-3 mb-2">
                      <div className="form-group">
                        <input
                          type="text"
                          name="company_name"
                          className="form-control"
                          placeholder="Company Name"
                          value={employment.company_name || ""}
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="col-md-3 mb-2">
                      <div className="form-group">
                        <input
                          type="text"
                          name="employee_code_number"
                          className="form-control"
                          placeholder="Employee Code/No"
                          value={employment.employee_code_number || ""}
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="col-md-3 mb-2">
                      <div className="form-group">
                        <input
                          type="text"
                          name="employee_designation"
                          className="form-control"
                          placeholder="Designation"
                          value={employment.employee_designation || ""}
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="col-md-3 mb-2">
                      <div className="form-group">
                        <input
                          type="text"
                          name="employee_department"
                          className="form-control"
                          placeholder="Department"
                          value={employment.employee_department || ""}
                          readOnly
                        />
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-3 mb-2">
                      <div className="form-group">
                        <input
                          type="text"
                          className="form-control"
                          name="manager_name"
                          placeholder="Manager's Name"
                          value={employment.manager_name || ""}
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="col-md-3 mb-2">
                      <div className="form-group">
                        <input
                          type="text"
                          className="form-control"
                          name="manager_email_id"
                          placeholder="Manager's Email ID"
                          value={employment.manager_email_id || ""}
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="col-md-3 mb-2">
                      <div className="form-group">
                        <input
                          type="text"
                          className="form-control"
                          name="manager_phone_number"
                          placeholder="Manager's Phone Number"
                          value={employment.manager_phone_number || ""}
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="col-md-3 mb-2">
                      <div className="form-group">
                        <input
                          type="text"
                          className="form-control"
                          name="employee_reference"
                          placeholder="Reference taken now (Yes/No)"
                          value={employment.employee_reference || ""}
                          readOnly
                        />
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-3 mb-2">
                      <div className="form-group">
                        <input
                          type="text"
                          className="form-control"
                          name="ctc_joining"
                          placeholder="CTC While Joining"
                          value={employment.ctc_joining || ""}
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="col-md-3 mb-2">
                      <div className="form-group">
                        <input
                          type="text"
                          className="form-control"
                          name="ctc_leaving"
                          placeholder="CTC While Leaving"
                          value={employment.ctc_leaving || ""}
                          readOnly
                        />
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-3 mb-2">
                      <div className="form-group">
                        <textarea
                          type="text"
                          className="form-control"
                          name="reasons_for_leaving"
                          placeholder="Reasons for Leaving"
                          value={employment.reasons_for_leaving || ""}
                          readOnly
                          rows={1}
                        />
                      </div>
                    </div>
                    <div className="col-md-3 mb-2">
                      <div className="form-group">
                        <textarea
                          type="text"
                          className="form-control"
                          name="employee_responsibilities"
                          placeholder="Responsibilities"
                          value={employment.employee_responsibilities || ""}
                          readOnly
                          rows={1}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-3 mb-2">
                      <div className="form-group">
                        <input
                          type="text"
                          className="form-control"
                          name="employment_type"
                          placeholder="Employment Type"
                          value={employment.employment_type || ""}
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="col-md-3 mb-2">
                      <div className="form-group">
                        <textarea
                          type="text"
                          className="form-control"
                          name="agency_details"
                          placeholder="Agency Details (if temporary/contractual)"
                          rows={1}
                          value={employment.agency_details || ""}
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="col-md-3 mb-2">
                      <div className="form-group">
                        <textarea
                          type="text"
                          className="form-control"
                          name="company_address"
                          placeholder="Address of Current Employer"
                          rows={1}
                          value={employment.company_address || ""}
                          readOnly
                        />
                      </div>
                    </div>
                  </div>

                  <div className="row text-center">
                    <div className="col-md-3">
                      <div className="form-group">
                        <label className="form-label">Start Date</label>
                        <input
                          type="date"
                          className="form-control"
                          name="employee_start_date"
                          value={employment.employee_start_date || ""}
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="form-group">
                        <label className="form-label">End Date</label>
                        <input
                          type="date"
                          className="form-control"
                          name="employee_end_date"
                          value={employment.employee_end_date || ""}
                          readOnly
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            <div className="mt-3 text-end">
              <h6>HR Verification</h6>
              <div className="form-check form-check-inline">
                <input
                  className="form-check-input border-dark"
                  type="radio"
                  name="employmentHistoryVerification"
                  id="employmentHistoryVerified"
                  checked={verifications.employmentHistory === "Verified"}
                  onChange={() =>
                    handleVerificationChange("employmentHistory", "Verified")
                  }
                />
                <label
                  className="form-check-label"
                  htmlFor="employmentHistoryVerified"
                >
                  Verified
                </label>
              </div>
              <div className="form-check form-check-inline">
                <input
                  className="form-check-input border-dark"
                  type="radio"
                  name="employmentHistoryVerification"
                  id="employmentHistoryNotVerified"
                  checked={verifications.employmentHistory === "Not Verified"}
                  onChange={() =>
                    handleVerificationChange(
                      "employmentHistory",
                      "Not Verified"
                    )
                  }
                />
                <label
                  className="form-check-label"
                  htmlFor="employmentHistoryNotVerified"
                >
                  Not Verified
                </label>
              </div>
            </div>
          </section>

          <hr />

          {/* Section: Education */}

          <section className="form-section">
            <h4 className="section-title">Education History </h4>

            {educationHistory.map((education, index) => (
              <div
                key={index}
                className="border border-dark p-2 mb-3 text-center"
              >
                <div className="row">
                  <div className="col-md-4 mb-2">
                    <div className="form-group">
                      <input
                        type="text"
                        name={`qualification-${index}`}
                        value={education.qualification || ""}
                        className="form-control"
                        placeholder="Post Graduation, Degree, PU, SSLC"
                        readOnly
                      />
                    </div>
                  </div>

                  <div className="col-md-4 mb-2">
                    <div className="form-group">
                      <input
                        type="text"
                        name={`college_name-${index}`}
                        value={education.college_name || ""}
                        className="form-control"
                        placeholder="College Name"
                        readOnly
                      />
                    </div>
                  </div>

                  <div className="col-md-4 mb-2">
                    <div className="form-group">
                      <input
                        type="text"
                        name={`university_board_name-${index}`}
                        value={education.university_board_name || ""}
                        className="form-control"
                        placeholder="University/Board Name"
                        readOnly
                      />
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-4 mb-2">
                    <div className="form-group">
                      <input
                        type="text"
                        name={`percentage_cgpa-${index}`}
                        value={education.percentage_cgpa || ""}
                        className="form-control"
                        placeholder="Percentage/CGPA"
                        readOnly
                      />
                    </div>
                  </div>
                  <div className="col-md-4 mb-2">
                    <div className="form-group">
                      <input
                        type="text"
                        name={`university_roll_number-${index}`}
                        value={education.university_roll_number || ""}
                        className="form-control"
                        placeholder="University/Roll Number"
                        readOnly
                      />
                    </div>
                  </div>

                  <div className="col-md-4 mb-2">
                    <div className="form-group">
                      <input
                        type="text"
                        name={`graduation_year-${index}`}
                        value={education.graduation_year || ""}
                        className="form-control"
                        placeholder="Graduation Year/Year of Pass"
                        readOnly
                      />
                    </div>
                  </div>
                </div>

                <div className="row text-center">
                  <div className="col-md-4">
                    <div className="form-group">
                      <label className="form-label">Type</label>
                      <input
                        type="text"
                        name={`education_type-${index}`}
                        value={education.education_type || ""}
                        className="form-control"
                        placeholder="Full Time/Part Time"
                        readOnly
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group">
                      <label className="form-label">Start Date</label>
                      <input
                        type="date"
                        name={`education_start_date-${index}`}
                        value={education.education_start_date || ""}
                        className="form-control"
                        readOnly
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group">
                      <label className="form-label">End Date</label>
                      <input
                        type="date"
                        name={`education_end_date-${index}`}
                        value={education.education_end_date || ""}
                        className="form-control"
                        readOnly
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
            <div className="mt-3 text-end">
              <h6>HR Verification</h6>
              <div className="form-check form-check-inline">
                <input
                  className="form-check-input border-dark"
                  type="radio"
                  name="educationHistoryVerification"
                  id="educationHistoryVerified"
                  checked={verifications.educationHistory === "Verified"}
                  onChange={() =>
                    handleVerificationChange("educationHistory", "Verified")
                  }
                />
                <label
                  className="form-check-label"
                  htmlFor="educationHistoryVerified"
                >
                  Verified
                </label>
              </div>
              <div className="form-check form-check-inline">
                <input
                  className="form-check-input border-dark"
                  type="radio"
                  name="educationHistoryVerification"
                  id="educationHistoryNotVerified"
                  checked={verifications.educationHistory === "Not Verified"}
                  onChange={() =>
                    handleVerificationChange("educationHistory", "Not Verified")
                  }
                />
                <label
                  className="form-check-label"
                  htmlFor="educationHistoryNotVerified"
                >
                  Not Verified
                </label>
              </div>
            </div>
          </section>

          <hr />

          {/* Skills Information Section */}
          <section className="form-section">
            <h4 className="text-center">Skills Information</h4>

            {/* Primary Skills */}
            <div className="row mb-2">
              <div className="col-md-2 d-flex align-items-start">
                <h5>
                  Primary Skills
                  <small className="text-muted" style={{ fontSize: "0.85rem" }}>
                    {" "}
                    (technologies where you have expertise)
                  </small>
                </h5>
              </div>
              <div className="col-md-10">
                {primarySkills.map((skill, index) => (
                  <div key={index} className="row mb-2">
                    <div className="col-md-3 mb-2">
                      <input
                        type="text"
                        className="form-control"
                        placeholder={`Primary Skill ${index + 1}`}
                        value={skill.skill_name}
                        readOnly
                      />
                    </div>
                    <div className="col-md-3 mb-2">
                      <input
                        type="number"
                        className="form-control"
                        placeholder="Experience (years)"
                        value={skill.experience}
                        readOnly
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Secondary Skills */}
            <div className="row mb-2">
              <div className="col-md-2 d-flex align-items-start">
                <h5 className="text-center">Secondary Skills </h5>
              </div>
              <div className="col-md-10">
                {secondarySkills.map((skill, index) => (
                  <div key={index} className="row mb-2">
                    <div className="col-md-3 mb-2">
                      <input
                        type="text"
                        className="form-control"
                        placeholder={`Secondary Skill ${index + 1}`}
                        value={skill.skill_name}
                        readOnly
                      />
                    </div>
                    <div className="col-md-3">
                      <input
                        type="number"
                        className="form-control"
                        placeholder="Experience (years)"
                        value={skill.experience}
                        readOnly
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-3 text-end">
              <h6>HR Verification</h6>
              <div className="form-check form-check-inline">
                <input
                  className="form-check-input border-dark"
                  type="radio"
                  name="skillsVerification"
                  id="skillsVerified"
                  checked={verifications.skills === "Verified"}
                  onChange={() =>
                    handleVerificationChange("skills", "Verified")
                  }
                />
                <label className="form-check-label" htmlFor="skillsVerified">
                  Verified
                </label>
              </div>
              <div className="form-check form-check-inline">
                <input
                  className="form-check-input border-dark"
                  type="radio"
                  name="skillsVerification"
                  id="skillsNotVerified"
                  checked={verifications.skills === "Not Verified"}
                  onChange={() =>
                    handleVerificationChange("skills", "Not Verified")
                  }
                />
                <label className="form-check-label" htmlFor="skillsNotVerified">
                  Not Verified
                </label>
              </div>
            </div>
          </section>

          <hr />

          {/* Section: Document Upload */}
          <section className="form-section">
            <h4 className="section-title">Documents to Upload</h4>
            <p className="text-danger text-start m-0">
              Supported formats: PDF, JPG, PNG,DOCX. Max file size: 5MB.
            </p>

            {/* Personal Documents Section */}
            <h5 className="sub-section-title text-center">
              Personal Documents
            </h5>
            {[
              "aadhar_card",
              "pan_card",
              "passport",
              "visa",
              "bank_passbook",
              "resume",
              "candidate_photo",
            ].map((doc, idx) => (
              <div className="form-group row" key={idx}>
                <label className="col-form-label col-md-3 col-12">
                  {doc
                    .replace(/_/g, " ")
                    .replace(/\b\w/g, (char) => char.toUpperCase())}
                  {doc === "resume" && (
                    <span className="text-muted">(Updated Resume)</span>
                  )}
                </label>
                <div className="col-12 col-md-6 mb-2">
                  {uploadedDocuments[doc] ? (
                    <p className="text-success">
                      Existing File:{" "}
                      <a
                        href={`${BASE_URL}${uploadedDocuments[doc]}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View Document
                      </a>
                    </p>
                  ) : (
                    <p className="text-muted">No file uploaded</p>
                  )}
                </div>
              </div>
            ))}

            {/* Education Section */}
            <h5 className="sub-section-title text-center">
              Education Documents
            </h5>
            {[
              "tenth_marksheet",
              "tenth_certificate",
              "pu_marksheet",
              "pu_certificate",
              "degree_consolidated_marksheet",
              "degree_certificate",
              "post_degree_marksheet",
              "post_degree_certificate",
            ].map((doc, idx) => (
              <div className="form-group row" key={idx}>
                <label className="col-form-label col-12 col-md-3">
                  {doc
                    .replace(/_/g, " ")
                    .replace(/\b\w/g, (char) => char.toUpperCase())}
                  {doc === "degree_consolidated_marksheet" && (
                    <span className="text-muted">
                      (Include all semesters as a single PDF)
                    </span>
                  )}
                </label>
                <div className="col-12 col-md-6 mb-2">
                  {uploadedDocuments[doc] ? (
                    <p className="text-success">
                      Existing File:{" "}
                      <a
                        href={`${BASE_URL}${uploadedDocuments[doc]}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View Document
                      </a>
                    </p>
                  ) : (
                    <p className="text-muted">No file uploaded</p>
                  )}
                </div>
              </div>
            ))}

            {/* Experience Documents Section */}
            <h5 className="sub-section-title text-center">
              Experience Documents
            </h5>
            {uploadedDocuments.experience_letters?.map((file, index) => (
              <div
                className="form-group row"
                key={`experience_letter_${index}`}
              >
                <label className="col-form-label col-12 col-md-3">
                  Experience Letter{" "}
                  {index === 0
                    ? "(Last/Current Company)"
                    : `(Company ${index})`}
                </label>
                <div className="col-12 col-md-6 mb-2">
                  {file ? (
                    <p className="text-success">
                      Existing File:{" "}
                      <a
                        href={`${BASE_URL}${file}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View Document
                      </a>
                    </p>
                  ) : (
                    <p className="text-muted">No file uploaded</p>
                  )}
                </div>
              </div>
            ))}

            {/* Relieving Documents Section */}
            <h5 className="sub-section-title text-center">
              Relieving Documents
            </h5>
            {uploadedDocuments.relieving_letters?.map((file, index) => (
              <div className="form-group row" key={`relieving_letter_${index}`}>
                <label className="col-form-label col-12 col-md-3">
                  Relieving Letter{" "}
                  {index === 0
                    ? "(Last/Current Company)"
                    : `(Company ${index})`}
                </label>
                <div className="col-12 col-md-6 mb-2">
                  {file ? (
                    <p className="text-success">
                      Existing File:{" "}
                      <a
                        href={`${BASE_URL}${file}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View Document
                      </a>
                    </p>
                  ) : (
                    <p className="text-muted">No file uploaded</p>
                  )}
                </div>
              </div>
            ))}

            {/* Add More Experience or Relieving Button */}

            <div className="mt-3 text-end">
              <h6>HR Verification</h6>
              <div className="form-check form-check-inline">
                <input
                  className="form-check-input border-dark"
                  type="radio"
                  name="documentsVerification"
                  id="documentsVerified"
                  checked={verifications.documents === "Verified"}
                  onChange={() =>
                    handleVerificationChange("documents", "Verified")
                  }
                />
                <label className="form-check-label" htmlFor="documentsVerified">
                  Verified
                </label>
              </div>
              <div className="form-check form-check-inline">
                <input
                  className="form-check-input border-dark"
                  type="radio"
                  name="documentsVerification"
                  id="documentsNotVerified"
                  checked={verifications.documents === "Not Verified"}
                  onChange={() =>
                    handleVerificationChange("documents", "Not Verified")
                  }
                />
                <label className="form-check-label" htmlFor="documentsVerified">
                  Not Verified
                </label>
              </div>
            </div>
          </section>

          <hr />
          {/* Section: Criminal Record */}
          <section className="form-section text-center">
            <h4 className="section-title">Criminal Record Declaration</h4>

            {/* Question 1: Have you ever been convicted of a criminal offense? */}
            <div className="form-group">
              <label htmlFor="criminal_record" className="form-label">
                Have you ever been convicted of a criminal offense?
              </label>
              <div>
                <div className="form-check form-check-inline">
                  <input
                    type="radio"
                    className="form-check-input border-dark"
                    name="criminal_record"
                    checked={criminalRecord.criminal_record === true}
                    disabled
                  />
                  <label className="form-check-label">Yes</label>
                </div>
                <div className="form-check form-check-inline">
                  <input
                    type="radio"
                    className="form-check-input border-dark"
                    name="criminal_record"
                    checked={criminalRecord.criminal_record === false}
                    onChange={() =>
                      setCriminalRecord((prev) => ({
                        ...prev,
                        criminal_record: false,
                      }))
                    }
                    disabled
                  />
                  <label className="form-check-label">No</label>
                </div>
              </div>
            </div>

            {/* Show additional fields if "Yes" is selected */}
            {criminalRecord.criminal_record && (
              <>
                {/* Question 2: Please provide details of the conviction */}
                <div className="form-group">
                  <label htmlFor="conviction_details" className="form-label">
                    Please provide details of the conviction:
                  </label>
                  <textarea
                    name="conviction_details"
                    className="form-control"
                    value={criminalRecord.conviction_details}
                    rows={1}
                    disabled
                  />
                </div>

                {/* Question 3: Have you completed any rehabilitation programs or served the sentence? */}
                <div className="form-group mt-2">
                  <label className="form-label">
                    Have you completed any rehabilitation programs or served the
                    sentence?
                  </label>
                  <div>
                    <div className="form-check form-check-inline">
                      <input
                        type="radio"
                        className="form-check-input border-dark"
                        name="completed_rehabilitation"
                        checked={
                          criminalRecord.completed_rehabilitation === true
                        }
                        onChange={() =>
                          setCriminalRecord((prev) => ({
                            ...prev,
                            completed_rehabilitation: true,
                          }))
                        }
                        disabled
                      />
                      <label className="form-check-label">Yes</label>
                    </div>
                    <div className="form-check form-check-inline">
                      <input
                        type="radio"
                        className="form-check-input border-dark"
                        name="completed_rehabilitation"
                        checked={
                          criminalRecord.completed_rehabilitation === false
                        }
                        onChange={() =>
                          setCriminalRecord((prev) => ({
                            ...prev,
                            completed_rehabilitation: false,
                          }))
                        }
                        disabled
                      />
                      <label className="form-check-label">No</label>
                    </div>
                  </div>
                </div>

                {/* Question 4: Do you have any pending criminal cases? */}
                <div className="form-group mt-2">
                  <label className="form-label">
                    Do you have any pending criminal cases?
                  </label>
                  <div>
                    <div className="form-check form-check-inline">
                      <input
                        type="radio"
                        className="form-check-input border-dark"
                        name="pending_cases"
                        checked={criminalRecord.pending_cases === true}
                        onChange={() =>
                          setCriminalRecord((prev) => ({
                            ...prev,
                            pending_cases: true,
                          }))
                        }
                        disabled
                      />
                      <label className="form-check-label">Yes</label>
                    </div>
                    <div className="form-check form-check-inline">
                      <input
                        type="radio"
                        className="form-check-input border-dark"
                        name="pending_cases"
                        checked={criminalRecord.pending_cases === false}
                        onChange={() =>
                          setCriminalRecord((prev) => ({
                            ...prev,
                            pending_cases: false,
                          }))
                        }
                        disabled
                      />
                      <label className="form-check-label">No</label>
                    </div>
                  </div>
                </div>
              </>
            )}
            <div className="mt-3 text-end">
              <h6>HR Verification</h6>
              <div className="form-check form-check-inline">
                <input
                  className="form-check-input border-dark"
                  type="radio"
                  name="criminalRecordVerification"
                  id="criminalRecordVerified"
                  checked={verifications.criminalRecord === "Verified"}
                  onChange={() =>
                    handleVerificationChange("criminalRecord", "Verified")
                  }
                />
                <label
                  className="form-check-label"
                  htmlFor="criminalRecordVerified"
                >
                  Verified
                </label>
              </div>
              <div className="form-check form-check-inline">
                <input
                  className="form-check-input border-dark"
                  type="radio"
                  name="criminalRecordVerification"
                  id="criminalRecordNotVerified"
                  checked={verifications.criminalRecord === "Not Verified"}
                  onChange={() =>
                    handleVerificationChange("criminalRecord", "Not Verified")
                  }
                />
                <label
                  className="form-check-label"
                  htmlFor="criminalRecordNotVerified"
                >
                  Not Verified
                </label>
              </div>
            </div>
          </section>

          <hr />
          {/* Section: Health Record */}
          <section className="form-section text-center">
            <h4 className="section-title">Health Record Declaration</h4>

            {/* Question 1: Do you have any medical conditions that may affect your work? */}
            <div className="form-group">
              <label htmlFor="medical_conditions" className="form-label">
                Do you have any medical conditions that may affect your work?
              </label>
              <div>
                <div className="form-check form-check-inline">
                  <input
                    type="radio"
                    className="form-check-input border-dark"
                    name="medical_conditions"
                    value={true}
                    checked={healthRecord.medical_conditions === true}
                    onChange={() =>
                      setHealthRecord({
                        ...healthRecord,
                        medical_conditions: true,
                      })
                    }
                    disabled
                  />
                  <label className="form-check-label">Yes</label>
                </div>
                <div className="form-check form-check-inline">
                  <input
                    type="radio"
                    className="form-check-input border-dark"
                    name="medical_conditions"
                    value={false}
                    checked={healthRecord.medical_conditions === false}
                    onChange={() =>
                      setHealthRecord({
                        ...healthRecord,
                        medical_conditions: false,
                      })
                    }
                    disabled
                  />
                  <label className="form-check-label">No</label>
                </div>
              </div>
            </div>

            {/* Show additional fields if "Yes" is selected for medical conditions */}
            {healthRecord.medical_conditions && (
              <div className="form-group">
                <label
                  htmlFor="medical_conditions_details"
                  className="form-label"
                >
                  Please provide details of the medical condition:
                </label>
                <textarea
                  name="medical_conditions_details"
                  className="form-control"
                  value={healthRecord.medical_conditions_details || ""}
                  onChange={(e) =>
                    setHealthRecord({
                      ...healthRecord,
                      medical_conditions_details: e.target.value,
                    })
                  }
                  disabled
                />
              </div>
            )}

            {/* Question 2: Have you undergone any recent health screening? */}
            <div className="form-group mt-2">
              <label className="form-label">
                Have you undergone any recent health screening?
              </label>
              <div>
                <div className="form-check form-check-inline">
                  <input
                    type="radio"
                    className="form-check-input border-dark"
                    name="recent_health_screening"
                    value={true}
                    checked={healthRecord.recent_health_screening === true}
                    onChange={() =>
                      setHealthRecord({
                        ...healthRecord,
                        recent_health_screening: true,
                      })
                    }
                    disabled
                  />
                  <label className="form-check-label">Yes</label>
                </div>
                <div className="form-check form-check-inline">
                  <input
                    type="radio"
                    className="form-check-input border-dark"
                    name="recent_health_screening"
                    value={false}
                    checked={healthRecord.recent_health_screening === false}
                    onChange={() =>
                      setHealthRecord({
                        ...healthRecord,
                        recent_health_screening: false,
                      })
                    }
                    disabled
                  />
                  <label className="form-check-label">No</label>
                </div>
              </div>
            </div>

            {/* Show additional fields if "Yes" is selected for recent health screening */}
            {healthRecord.recent_health_screening && (
              <div className="form-group">
                <label
                  htmlFor="health_screening_details"
                  className="form-label"
                >
                  Please provide details of the health screening:
                </label>
                <textarea
                  name="health_screening_details"
                  className="form-control"
                  value={healthRecord.health_screening_details || ""}
                  onChange={(e) =>
                    setHealthRecord({
                      ...healthRecord,
                      health_screening_details: e.target.value,
                    })
                  }
                  disabled
                />
              </div>
            )}

            {/* Question 3: Do you have any allergies or other conditions that we should be aware of? */}
            <div className="form-group mt-2">
              <label className="form-label">
                Do you have any allergies or other conditions that we should be
                aware of?
              </label>
              <div>
                <div className="form-check form-check-inline">
                  <input
                    type="radio"
                    className="form-check-input border-dark"
                    name="allergies_or_conditions"
                    value={true}
                    checked={healthRecord.allergies_or_conditions === true}
                    onChange={() =>
                      setHealthRecord({
                        ...healthRecord,
                        allergies_or_conditions: true,
                      })
                    }
                    disabled
                  />
                  <label className="form-check-label">Yes</label>
                </div>
                <div className="form-check form-check-inline">
                  <input
                    type="radio"
                    className="form-check-input border-dark"
                    name="allergies_or_conditions"
                    value={false}
                    checked={healthRecord.allergies_or_conditions === false}
                    onChange={() =>
                      setHealthRecord({
                        ...healthRecord,
                        allergies_or_conditions: false,
                      })
                    }
                    disabled
                  />
                  <label className="form-check-label">No</label>
                </div>
              </div>
            </div>

            {/* Show additional fields if "Yes" is selected for allergies or conditions */}
            {healthRecord.allergies_or_conditions === "yes" && (
              <div className="form-group">
                <label
                  htmlFor="emergency_condition_details"
                  className="form-label"
                >
                  Please provide details of the allergy or condition:
                </label>
                <textarea
                  name="emergency_condition_details"
                  className="form-control"
                  value={healthRecord.emergency_condition_details || ""}
                  onChange={(e) =>
                    setHealthRecord({
                      ...healthRecord,
                      allergies_or_conditions: e.target.value,
                    })
                  }
                  disabled
                />
              </div>
            )}

            {/* Question 4: Do you consent to company-mandated health screenings? */}
            <div className="form-group mt-2">
              <label className="form-label">
                Do you consent to company-mandated health screenings?
              </label>
              <div>
                <div className="form-check form-check-inline">
                  <input
                    type="radio"
                    className="form-check-input border-dark"
                    name="health_check_consent"
                    value={true}
                    checked={healthRecord.health_check_consent === true}
                    onChange={() =>
                      setHealthRecord({
                        ...healthRecord,
                        health_check_consent: true,
                      })
                    }
                    disabled
                  />
                  <label className="form-check-label">Yes</label>
                </div>
                <div className="form-check form-check-inline">
                  <input
                    type="radio"
                    className="form-check-input border-dark"
                    name="health_check_consent"
                    value={false}
                    checked={healthRecord.health_check_consent === false}
                    onChange={() =>
                      setHealthRecord({
                        ...healthRecord,
                        health_check_consent: false,
                      })
                    }
                    disabled
                  />
                  <label className="form-check-label">No</label>
                </div>
              </div>
            </div>
            <div className="mt-3 text-end">
              <h6>HR Verification</h6>
              <div className="form-check form-check-inline">
                <input
                  className="form-check-input border-dark"
                  type="radio"
                  name="healthRecordVerification"
                  id="healthRecordVerified"
                  checked={verifications.healthRecord === "Verified"}
                  onChange={() =>
                    handleVerificationChange("healthRecord", "Verified")
                  }
                />
                <label
                  className="form-check-label"
                  htmlFor="healthRecordVerified"
                >
                  Verified
                </label>
              </div>
              <div className="form-check form-check-inline">
                <input
                  className="form-check-input border-dark"
                  type="radio"
                  name="healthRecordVerification"
                  id="healthRecordNotVerified"
                  checked={verifications.healthRecord === "Not Verified"}
                  onChange={() =>
                    handleVerificationChange("healthRecord", "Not Verified")
                  }
                />
                <label
                  className="form-check-label"
                  htmlFor="healthRecordNotVerified"
                >
                  Not Verified
                </label>
              </div>
            </div>
          </section>

          {/* Save or Submit */}
          <div className="text-center mt-4">
            <button
              type="button"
              className="btn btn-success"
              onClick={handleSubmitVerification}
            >
              Submit Verification
            </button>
            <button
              type="button"
              className="btn btn-primary ms-2"
              onClick={() => navigate("/employee_dashboard")}
            >
              Back to Dashboard
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default CandidateVerificationForm;
